export class NonReplayableValueException extends Error {
  constructor(
    readonly path: string,
    readonly reason: string
  ) {
    super(`Non-replayable value at ${path}: ${reason}`)
    this.name = 'NonReplayableValueException'
  }
}

/**
 * Traces reproduce values, not object behavior. This accepts the plain data
 * model used by module state/messages and rejects values whose prototype,
 * identity, accessor, or host behavior would make replay environment-specific.
 */
export function assertReplayable(value: unknown, path: string): void {
  inspectReplayable(value, path, new WeakSet<object>())
}

function inspectReplayable(value: unknown, path: string, ancestors: WeakSet<object>): void {
  if (value == null || typeof value == 'string' || typeof value == 'boolean') {
    return
  }

  if (typeof value == 'number') {
    if (!Number.isFinite(value)) {
      throw new NonReplayableValueException(path, 'number must be finite')
    }
    return
  }

  if (typeof value == 'undefined') {
    return
  }

  if (typeof value != 'object') {
    throw new NonReplayableValueException(path, `${typeof value} values are not data`)
  }

  if (ancestors.has(value)) {
    throw new NonReplayableValueException(path, 'cyclic references are not supported')
  }

  if (!Array.isArray(value)) {
    const prototype = Object.getPrototypeOf(value)
    if (prototype != Object.prototype && prototype != null) {
      const constructor = Object.getOwnPropertyDescriptor(prototype, 'constructor')?.value
      const name = typeof constructor == 'function' ? constructor.name : 'non-plain object'
      throw new NonReplayableValueException(path, `${name} instance`)
    }
  }

  if (Object.getOwnPropertySymbols(value).length > 0) {
    throw new NonReplayableValueException(path, 'symbol-keyed properties are not supported')
  }

  ancestors.add(value)
  try {
    const descriptors = Object.getOwnPropertyDescriptors(value)
    for (const [key, descriptor] of Object.entries(descriptors)) {
      if (Array.isArray(value) && key == 'length') {
        continue
      }
      if ('get' in descriptor || 'set' in descriptor) {
        throw new NonReplayableValueException(
          childPath(path, key),
          'accessor properties are not data'
        )
      }
      inspectReplayable(descriptor.value, childPath(path, key), ancestors)
    }
  } finally {
    ancestors.delete(value)
  }
}

function childPath(parent: string, key: string): string {
  return /^\d+$/.test(key) ? `${parent}[${key}]` : `${parent}.${key}`
}
